import React, { useState, useEffect, useRef } from 'react';
import { X, MapPin, Loader2, Send, Package, User, Phone, ArrowDown } from 'lucide-react';
import { createDelivery } from './deliveryService';
import { getRouteEstimate, calculateDeliveryFare } from './fareEngine';
import AdBanner from './AdBanner';

const SIZES = [
  { key: 'small', label: 'קטן', desc: 'מעטפה / חבילה קטנה' },
  { key: 'medium', label: 'בינוני', desc: 'שקית קניות / קופסה' },
  { key: 'large', label: 'גדול', desc: 'חבילה גדולה / כמה פריטים' },
];

export default function DeliveryRequestForm({ userProfile, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);

  const [pickup, setPickup] = useState({ address: '', lat: null, lng: null });
  const [dropoff, setDropoff] = useState({ address: '', lat: null, lng: null });
  const [size, setSize] = useState('small');
  const [parcel, setParcel] = useState({ description: '', recipientName: '', recipientPhone: '', notes: '' });

  const [estimate, setEstimate] = useState(null); // { distanceKm, durationMin }
  const [fare, setFare] = useState(null);
  const [estimating, setEstimating] = useState(false);

  const pickupRef = useRef(null);
  const dropoffRef = useRef(null);

  // --- חיבור Autocomplete של גוגל לשני השדות ---
  useEffect(() => {
    if (!window.google || !window.google.maps) return;

    const attach = (ref, setter) => {
      if (!ref.current) return;
      const ac = new window.google.maps.places.Autocomplete(ref.current, {
        componentRestrictions: { country: 'il' },
      });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (place.geometry) {
          setter({
            address: place.formatted_address,
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });
        }
      });
    };

    attach(pickupRef, setPickup);
    attach(dropoffRef, setDropoff);
  }, []);

  // --- חישוב מחיר חי: כשיש שתי כתובות תקינות או כשמשנים גודל ---
  useEffect(() => {
    const run = async () => {
      if (!pickup.lat || !dropoff.lat) {
        setEstimate(null);
        setFare(null);
        return;
      }
      setEstimating(true);
      try {
        const est = await getRouteEstimate(pickup, dropoff);
        setEstimate(est);
        setFare(calculateDeliveryFare(est.distanceKm, size));
      } catch (e) {
        console.error('Route estimate failed:', e);
        setEstimate(null);
        setFare(null);
      } finally {
        setEstimating(false);
      }
    };
    run();
  }, [pickup.lat, pickup.lng, dropoff.lat, dropoff.lng, size]);

  const handleSubmit = async () => {
    if (!pickup.lat || !dropoff.lat) return alert('חובה לבחור כתובת איסוף וכתובת מסירה מתוך הרשימה');
    if (!parcel.description.trim()) return alert('מה שולחים? נא למלא תיאור קצר');
    if (!fare) return alert('עוד רגע — מחשב את המחיר');

    setLoading(true);
    try {
      await createDelivery({
        sender: userProfile,
        pickup,
        dropoff,
        parcel: { ...parcel, size },
        estimate: { distanceKm: estimate.distanceKm, durationMin: estimate.durationMin, fare },
        paymentMethod: 'cash',
      });
      alert('המשלוח פורסם! שליחים באזור יקבלו התראה 🚀');
      onSuccess();
    } catch (e) {
      console.error(e);
      alert('שגיאה בפרסום המשלוח');
      setLoading(false);
    }
  };

  const addrInputClass = (loc) =>
    `w-full border rounded-xl p-3 text-sm outline-none ${
      !loc.lat && loc.address.length > 0
        ? 'border-red-300 bg-red-50'
        : 'border-gray-200 bg-gray-50 focus:border-black'
    }`;

  return (
    <div className="h-full flex flex-col bg-white text-right" dir="rtl">

      {/* כותרת */}
      <div className="p-4 text-white flex justify-between items-center shadow-md sticky top-0 z-10 bg-blue-600">
        <h2 className="font-bold text-lg flex items-center gap-2"><Package size={20} /> שליחת חבילה</h2>
        <button onClick={onClose} className="bg-white/20 p-2 rounded-full"><X size={20} /></button>
      </div>

      {/* תוכן נגלל */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5 pb-32">

        {/* כתובות איסוף ומסירה */}
        <div className="space-y-2">
          <div className="relative">
            <span className="absolute top-3.5 right-3 w-2.5 h-2.5 rounded-full bg-green-500 ring-4 ring-green-100"></span>
            <input
              ref={pickupRef}
              type="text"
              className={`${addrInputClass(pickup)} pr-9`}
              placeholder="כתובת איסוף — מאיפה לוקחים?"
              defaultValue=""
              onChange={(e) => setPickup({ address: e.target.value, lat: null, lng: null })}
            />
          </div>

          <div className="flex justify-center text-gray-300"><ArrowDown size={16} /></div>

          <div className="relative">
            <span className="absolute top-3.5 right-3 w-2.5 h-2.5 rounded-sm bg-red-500 ring-4 ring-red-100"></span>
            <input
              ref={dropoffRef}
              type="text"
              className={`${addrInputClass(dropoff)} pr-9`}
              placeholder="כתובת מסירה — לאן מביאים?"
              defaultValue=""
              onChange={(e) => setDropoff({ address: e.target.value, lat: null, lng: null })}
            />
          </div>
        </div>

        {/* גודל החבילה */}
        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-700 flex items-center gap-2"><Package size={16} /> גודל החבילה</label>
          <div className="grid grid-cols-3 gap-2">
            {SIZES.map((s) => (
              <button
                key={s.key}
                onClick={() => setSize(s.key)}
                className={`p-3 rounded-xl border text-center transition-all ${
                  size === s.key ? 'bg-black text-white border-black' : 'bg-gray-50 text-gray-600 border-gray-200'
                }`}
              >
                <div className="font-bold text-sm">{s.label}</div>
                <div className={`text-[10px] mt-0.5 ${size === s.key ? 'text-gray-300' : 'text-gray-400'}`}>{s.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* תיאור */}
        <textarea
          rows={2}
          className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 outline-none resize-none focus:border-black text-sm"
          placeholder="מה שולחים? (לדוגמה: מסמכים, תרופות מבית מרקחת, שקית קניות)"
          value={parcel.description}
          onChange={(e) => setParcel({ ...parcel, description: e.target.value })}
        />

        {/* פרטי המקבל */}
        <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 space-y-2">
          <label className="text-sm font-bold text-gray-700">פרטי המקבל בקצה השני</label>
          <div className="relative">
            <User className="absolute right-3 top-3 text-gray-400" size={18} />
            <input
              type="text"
              className="w-full bg-white border border-gray-200 rounded-lg p-2.5 pr-9 text-sm outline-none focus:border-black"
              placeholder="שם המקבל"
              value={parcel.recipientName}
              onChange={(e) => setParcel({ ...parcel, recipientName: e.target.value })}
            />
          </div>
          <div className="relative">
            <Phone className="absolute right-3 top-3 text-gray-400" size={18} />
            <input
              type="tel"
              dir="ltr"
              className="w-full bg-white border border-gray-200 rounded-lg p-2.5 pr-9 text-sm outline-none focus:border-black text-right"
              placeholder="טלפון המקבל"
              value={parcel.recipientPhone}
              onChange={(e) => setParcel({ ...parcel, recipientPhone: e.target.value })}
            />
          </div>
        </div>

        {/* כרטיס מחיר */}
        {(estimating || fare) && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center justify-between">
            {estimating ? (
              <span className="text-blue-600 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> מחשב מחיר...</span>
            ) : (
              <>
                <div>
                  <p className="text-xs text-blue-500">מחיר משוער (במזומן לשליח)</p>
                  <p className="text-2xl font-black text-blue-700">₪{fare}</p>
                </div>
                <div className="text-left text-xs text-blue-400">
                  <p>{estimate.distanceKm} ק"מ</p>
                  <p>~{estimate.durationMin} דק'</p>
                </div>
              </>
            )}
          </div>
        )}

        <AdBanner type="banner" />
      </div>

      {/* כפתור פרסום */}
      <div className="p-4 pb-16 border-t bg-gray-50 z-20 relative shadow-[0_-5px_20px_rgba(0,0,0,0.1)]">
        <button
          onClick={handleSubmit}
          disabled={loading || estimating || !fare}
          className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg flex items-center justify-center gap-2 transition-all active:scale-95 ${
            !fare || loading || estimating ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white'
          }`}
        >
          {loading ? <Loader2 className="animate-spin" /> : <>פרסם משלוח <Send size={20} /></>}
        </button>
      </div>
    </div>
  );
}
